/**
 * DecisionFlow — TenantList
 * ui/src/components/platform-admin/TenantList.jsx
 * OI-S225-01 · S626: multi-select bulk delete, created_at + tenant_id columns,
 *              server-side pagination, scrollable table
 *
 * Fetches GET /api/v1/admin/tenants (tool_admin only).
 * Envelope: { tenants: [...], total: N }
 *
 * Pagination: server-side via limit/offset. PAGE_SIZE=25.
 * Table body: fixed-height scrollable container (no full-page scroll).
 * Multi-select: checkboxes on is_demo rows only; non-demo rows disabled.
 * Bulk delete: serial calls to existing single deprovision endpoint.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshCw, Trash2, Search, X, ChevronLeft, ChevronRight } from "lucide-react";

import Card from "../shared/Card.jsx";
import Button from "../shared/Button.jsx";
import TenantDeprovision from "./TenantDeprovision.jsx";

const API_BASE   = "/api/v1";
const PAGE_SIZE  = 25;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-2 py-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="h-10 w-full rounded skeleton" />
      ))}
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message}
    </div>
  );
}

function StatusPill({ status }) {
  const colorMap = {
    active:        "text-[rgb(var(--color-success))]",
    trial:         "text-[rgb(var(--color-primary))]",
    suspended:     "text-[rgb(var(--color-warning))]",
    deprovisioned: "text-[rgb(var(--color-danger))]",
  };
  return (
    <span className={`text-tok-xs font-medium ${colorMap[status] ?? "text-[rgb(var(--color-text-muted))]"}`}>
      {status}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return "—";
  }
}

// ---------------------------------------------------------------------------
// Pagination bar
// ---------------------------------------------------------------------------

function PaginationBar({ page, pageSize, total, onPrev, onNext, loading }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to   = Math.min((page + 1) * pageSize, total);

  return (
    <div className="flex items-center justify-between gap-3 border-t border-[rgb(var(--color-border))] px-1 pt-3 mt-2">
      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
        {total === 0 ? "No results" : `${from}–${to} of ${total}`}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          onClick={onPrev}
          disabled={page === 0 || loading}
          className="flex items-center gap-1 rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Prev
        </button>
        <span className="px-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          {page + 1} / {totalPages}
        </span>
        <button
          onClick={onNext}
          disabled={(page + 1) * pageSize >= total || loading}
          className="flex items-center gap-1 rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          Next <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TenantList
// ---------------------------------------------------------------------------

export default function TenantList() {
  const { t } = useTranslation("platform");

  const [tenants,  setTenants]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [query,    setQuery]    = useState("");
  const [sortBy,   setSortBy]   = useState("created_at");
  const [sortDir,  setSortDir]  = useState("desc");
  const [page,     setPage]     = useState(0);
  const [demoOnly, setDemoOnly] = useState(false);

  // Multi-select — Set of tenant_id strings (demo tenants only, current page)
  const [selected, setSelected] = useState(new Set());

  // Deprovision modal — null = closed, array of tenant objects = open
  const [deprovisionTargets, setDeprovisionTargets] = useState(null);

  const debounceRef = useRef(null);

  const fetchTenants = useCallback(async (
    searchQuery = "", sb = sortBy, sd = sortDir, pg = page, demo = demoOnly
  ) => {
    setLoading(true);
    setError(null);
    setSelected(new Set());
    try {
      const params = new URLSearchParams({
        limit:    String(PAGE_SIZE),
        offset:   String(pg * PAGE_SIZE),
        sort_by:  sb,
        sort_dir: sd,
      });
      if (searchQuery.trim()) params.set("q", searchQuery.trim());
      if (demo) params.set("is_demo", "true");

      const res = await fetch(`${API_BASE}/admin/tenants?${params}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.error?.message ?? `Error ${res.status}`);
      }
      const data = await res.json();
      setTenants(data.tenants ?? []);
      setTotal(data.total ?? 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortDir, page]);

  useEffect(() => {
    fetchTenants(query, sortBy, sortDir, page);
  }, [fetchTenants]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleQueryChange(e) {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      fetchTenants(val, sortBy, sortDir, 0, demoOnly);
    }, 300);
  }

  function handleClear() {
    setQuery("");
    setPage(0);
    fetchTenants("", sortBy, sortDir, 0, demoOnly);
  }

  function handleSort(col) {
    const newDir = col === sortBy && sortDir === "desc" ? "asc" : "desc";
    setSortBy(col);
    setSortDir(newDir);
    setPage(0);
    fetchTenants(query, col, newDir, 0, demoOnly);
  }

  function handlePrev() {
    const pg = Math.max(0, page - 1);
    setPage(pg);
    fetchTenants(query, sortBy, sortDir, pg, demoOnly);
  }

  function handleNext() {
    const pg = page + 1;
    setPage(pg);
    fetchTenants(query, sortBy, sortDir, pg, demoOnly);
  }

  function handleDemoToggle() {
    const next = !demoOnly;
    setDemoOnly(next);
    setPage(0);
    fetchTenants(query, sortBy, sortDir, 0, next);
  }

  // ---------------------------------------------------------------------------
  // Selection helpers (scoped to current page)
  // ---------------------------------------------------------------------------

  const demoTenants    = tenants.filter((t) => t.is_demo);
  const allDemoSelected = demoTenants.length > 0 && demoTenants.every((t) => selected.has(t.tenant_id));
  const someSelected    = selected.size > 0;

  function toggleSelectAll() {
    if (allDemoSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(demoTenants.map((t) => t.tenant_id)));
    }
  }

  function toggleOne(tenantId) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(tenantId) ? next.delete(tenantId) : next.add(tenantId);
      return next;
    });
  }

  // ---------------------------------------------------------------------------
  // Deprovision
  // ---------------------------------------------------------------------------

  function openSingle(tenant) {
    setDeprovisionTargets([tenant]);
  }

  function openBulk() {
    const targets = tenants.filter((t) => selected.has(t.tenant_id));
    if (targets.length > 0) setDeprovisionTargets(targets);
  }

  function handleDeprovisionSuccess(deletedIds) {
    setDeprovisionTargets(null);
    setSelected(new Set());
    // Refresh current page — total has changed, may need to step back a page
    const newTotal = total - deletedIds.length;
    const maxPage  = Math.max(0, Math.ceil(newTotal / PAGE_SIZE) - 1);
    const nextPage = Math.min(page, maxPage);
    setPage(nextPage);
    setTotal(newTotal);
    fetchTenants(query, sortBy, sortDir, nextPage, demoOnly);
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <Card
        title={t("tenantList.title")}
        action={
          <Button
            variant="secondary"
            onClick={() => fetchTenants(query, sortBy, sortDir, page, demoOnly)}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {t("tenantList.refresh")}
          </Button>
        }
      >
        {/* Search bar */}
        <div className="mb-3 relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--color-text-muted))]" />
          <input
            type="text"
            value={query}
            onChange={handleQueryChange}
            placeholder="Search by name, slug, or tenant ID…"
            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] py-2 pl-9 pr-9 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))]"
          />
          {query && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Demo-only toggle */}
        <div className="mb-3 flex items-center gap-2">
          <button
            onClick={handleDemoToggle}
            className={[
              "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] focus:ring-offset-1",
              demoOnly ? "bg-[rgb(var(--color-primary))]" : "bg-[rgb(var(--color-border))]",
            ].join(" ")}
            role="switch"
            aria-checked={demoOnly}
          >
            <span
              className={[
                "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200",
                demoOnly ? "translate-x-4" : "translate-x-0",
              ].join(" ")}
            />
          </button>
          <span
            className="text-tok-xs font-medium cursor-pointer select-none text-[rgb(var(--color-text))]"
            onClick={handleDemoToggle}
          >
            Show demo tenants only
          </span>
          {demoOnly && (
            <span className="rounded-full bg-[rgb(var(--color-primary))]/15 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-primary))]">
              Filter active
            </span>
          )}
        </div>

        {/* Summary row */}
        <div className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
          {!loading && !error && (
            query.trim()
              ? <span>{total} result{total !== 1 ? "s" : ""} for <span className="font-mono">"{query.trim()}"</span></span>
              : <span>{t("tenantList.total", { count: total })}</span>
          )}
        </div>

        {/* Bulk delete bar */}
        {someSelected && (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-4 py-2.5">
            <span className="text-tok-sm font-medium text-[rgb(var(--color-danger))]">
              {selected.size} demo tenant{selected.size !== 1 ? "s" : ""} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected(new Set())}
                className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition"
              >
                Clear
              </button>
              <Button variant="danger" onClick={openBulk}>
                <Trash2 className="h-3.5 w-3.5" />
                Delete {selected.size} tenant{selected.size !== 1 ? "s" : ""}
              </Button>
            </div>
          </div>
        )}

        {loading && <LoadingSkeleton />}
        {error   && <ErrorNote message={error} />}

        {!loading && !error && tenants.length === 0 && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            {query.trim()
              ? <>No tenants match <span className="font-mono">"{query.trim()}"</span>.</>
              : t("tenantList.empty")
            }
          </p>
        )}

        {!loading && !error && tenants.length > 0 && (
          <>
            {/* Scrollable table — fixed height, thead sticky */}
            <div className="overflow-auto rounded-xl border border-[rgb(var(--color-border))]" style={{ maxHeight: "calc(100vh - 320px)", minHeight: "320px" }}>
              <table className="w-full border-collapse text-tok-sm">
                <thead className="sticky top-0 z-10 bg-[rgb(var(--color-surface))]">
                  <tr className="border-b border-[rgb(var(--color-border))]">
                    {/* Select-all */}
                    <th className="w-8 py-2 pl-3 pr-2 text-left">
                      <input
                        type="checkbox"
                        checked={allDemoSelected}
                        onChange={toggleSelectAll}
                        disabled={demoTenants.length === 0}
                        title={demoTenants.length === 0 ? "No demo tenants on this page" : "Select all demo tenants on this page"}
                        className="rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))] disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                      />
                    </th>
                    {[
                      { key: "name",    col: "tenant_name",        label: t("tenantList.col.name") },
                      { key: "slug",    col: "tenant_slug",        label: t("tenantList.col.slug") },
                      { key: "uuid",    col: null,                 label: "Tenant ID" },
                      { key: "tier",    col: "subscription_tier",  label: t("tenantList.col.tier") },
                      { key: "status",  col: "tenant_status_code", label: t("tenantList.col.status") },
                      { key: "demo",    col: "is_demo",            label: t("tenantList.col.demo") },
                      { key: "created", col: "created_at",         label: "Created" },
                      { key: "seats",   col: null,                 label: "Seats" },
                      { key: "actions", col: null,                 label: t("tenantList.col.actions") },
                    ].map(({ key, col, label }) => (
                      <th
                        key={key}
                        onClick={col ? () => handleSort(col) : undefined}
                        className={[
                          "py-2 pr-4 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))] whitespace-nowrap",
                          key === "actions" ? "text-right pr-3" : "text-left",
                          col ? "cursor-pointer select-none hover:text-[rgb(var(--color-text))] transition" : "",
                        ].join(" ")}
                      >
                        {label}
                        {col && sortBy === col && (
                          <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((tenant) => {
                    const isSelected = selected.has(tenant.tenant_id);
                    return (
                      <tr
                        key={tenant.tenant_id}
                        className={[
                          "border-b border-[rgb(var(--color-border))] last:border-0 transition",
                          isSelected
                            ? "bg-[rgb(var(--color-danger))]/5"
                            : "hover:bg-[rgb(var(--color-surface-muted))]",
                        ].join(" ")}
                      >
                        <td className="py-2.5 pl-3 pr-2 w-8">
                          {tenant.is_demo ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleOne(tenant.tenant_id)}
                              className="rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))] cursor-pointer"
                            />
                          ) : (
                            <input
                              type="checkbox"
                              disabled
                              title="Live tenants cannot be deleted via this interface"
                              className="rounded border-[rgb(var(--color-border))] opacity-25 cursor-not-allowed"
                            />
                          )}
                        </td>
                        <td className="py-2.5 pr-4 font-medium whitespace-nowrap">
                          <Link
                            to={`/platform-admin/tenants/${tenant.tenant_id}`}
                            className="text-[rgb(var(--color-primary))] hover:underline"
                          >
                            {tenant.tenant_name}
                          </Link>
                        </td>
                        <td className="py-2.5 pr-4 font-mono text-tok-xs text-[rgb(var(--color-text))] whitespace-nowrap">
                          {tenant.tenant_slug}
                        </td>
                        <td className="py-2.5 pr-4 font-mono text-tok-xs text-[rgb(var(--color-text-muted))] max-w-[140px] truncate" title={tenant.tenant_id}>
                          {tenant.tenant_id}
                        </td>
                        <td className="py-2.5 pr-4 text-[rgb(var(--color-text))] whitespace-nowrap">
                          {tenant.subscription_tier ?? "—"}
                        </td>
                        <td className="py-2.5 pr-4 whitespace-nowrap">
                          <StatusPill status={tenant.tenant_status_code ?? "active"} />
                        </td>
                        <td className="py-2.5 pr-4 whitespace-nowrap">
                          {tenant.is_demo
                            ? <span className="text-tok-xs font-medium text-[rgb(var(--color-warning))]">{t("tenantList.demo")}</span>
                            : <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{t("tenantList.live")}</span>
                          }
                        </td>
                        <td className="py-2.5 pr-4 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                          {formatDate(tenant.created_at)}
                        </td>
                        <td className="py-2.5 pr-4 whitespace-nowrap">
                          {(() => {
                            const active    = tenant.active_position_count ?? null;
                            const purchased = tenant.max_positions ?? null;
                            if (active === null || purchased === null) {
                              return <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">—</span>;
                            }
                            const pct = purchased > 0 ? (active / purchased) * 100 : 0;
                            const colour = active > purchased
                              ? "text-[rgb(var(--color-danger))] font-semibold"
                              : pct >= 80
                              ? "text-[rgb(var(--color-warning))] font-medium"
                              : "text-[rgb(var(--color-success))]";
                            return (
                              <span className={`text-tok-xs ${colour}`}>
                                {active}/{purchased}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-2.5 pr-3 text-right whitespace-nowrap">
                          {tenant.is_demo && (
                            <Button variant="danger" onClick={() => openSingle(tenant)}>
                              <Trash2 className="h-3.5 w-3.5" />
                              {t("tenantList.delete")}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination bar — below the table */}
            <PaginationBar
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPrev={handlePrev}
              onNext={handleNext}
              loading={loading}
            />
          </>
        )}
      </Card>

      {deprovisionTargets && (
        <TenantDeprovision
          tenants={deprovisionTargets}
          onSuccess={handleDeprovisionSuccess}
          onCancel={() => setDeprovisionTargets(null)}
        />
      )}
    </>
  );
}
